import React, { useState, useEffect } from 'react';
import Button from '../../../NecttosComp/Button/Button';

import { Course, UserRole } from '../../types/lms.types';
import { getMockCourses, getDashboardStats } from '../../services/lmsDataService';

interface LMSDashboardProps {
  userRole: UserRole;
  userName: string;
  collegeData: any;
  onCourseSelect: (course: Course) => void;
}

const LMSDashboard: React.FC<LMSDashboardProps> = ({
  userRole,
  userName,
  collegeData,
  onCourseSelect
}) => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('courses');

  useEffect(() => {
    const loadData = async () => {
      try {
        const mockCourses = getMockCourses(userRole);
        setCourses(mockCourses);
      } catch (error) {
        console.error('Error loading courses:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userRole]);

  const stats = getDashboardStats(userRole);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Welcome Section */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Welcome back, {userName}!
        </h2>
        <p className="text-gray-600">
          Here's your learning dashboard for {collegeData?.name || 'your institution'}.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <span className="text-blue-600 text-xl">{stat.icon}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {['courses', 'assignments', 'announcements', 'analytics'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Courses Tab */}
      {activeTab === 'courses' && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              {userRole === 'student' ? 'Enrolled Courses' : 
               userRole === 'teacher' ? 'My Courses' : 'All Courses'}
            </h3>
            {userRole === 'teacher' && (
              <Button type="save">
                Create Course
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => (
              <div key={course.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow p-6">
                <div className="flex items-start justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-900 flex-1">
                    {course.name}
                  </h4>
                  {course.isLive && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      <span className="w-2 h-2 bg-red-400 rounded-full mr-1 animate-pulse"></span>
                      Live
                    </span>
                  )}
                </div>
                
                <p className="text-sm text-gray-600 mb-4">
                  Instructor: {course.teacher}
                </p>
                
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Students</span>
                    <span className="font-medium">{course.students}</span>
                  </div>
                  
                  {userRole === 'student' && course.progress !== undefined && (
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Progress</span>
                        <span className="font-medium">{course.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${course.progress}%` }}
                        />
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>{course.assignments} assignments</span>
                    <span>{course.announcements} announcements</span>
                  </div>
                </div>

                <div className="w-full">
                  <Button
                    type="view"
                    onClick={() => onCourseSelect(course)}
                  >
                    Open Course
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Other tabs content */}
      {activeTab !== 'courses' && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-2 capitalize">
            {activeTab}
          </h3>
          <p className="text-gray-600">
            {activeTab} management will be shown here.
          </p>
        </div>
      )}
    </div>
  );
};

export default LMSDashboard;
